import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiErr, downloadFile } from "@/lib/api";
import { Card, Loader } from "@/components/ui-helpers";
import { toast } from "sonner";
import { PenLine, Plus, Copy, Send, Download, Ban, Trash2, FileSignature } from "lucide-react";

const BADGE = {
  Draft: "bg-slate-100 text-slate-600",
  Sent: "bg-amber-100 text-amber-700",
  Completed: "bg-emerald-100 text-emerald-700",
  Voided: "bg-rose-100 text-rose-700",
};

export default function Esign() {
  const [rows, setRows] = useState(null);
  const nav = useNavigate();
  const load = () => api.get("/esign/envelopes").then((r) => setRows(r.data)).catch(() => setRows([]));
  useEffect(() => { load(); }, []);

  const copyLink = async (e) => {
    try { const r = await api.get(`/esign/envelopes/${e.id}/link`); await navigator.clipboard.writeText(r.data.url); toast.success(`Link for ${r.data.recipient} copied`); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const resend = async (e) => {
    try { const r = await api.post(`/esign/envelopes/${e.id}/resend`); toast.success(`Email resent to ${r.data.sent_to}`); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const voidEnv = async (e) => {
    if (!window.confirm(`Void \"${e.title}\"? Signers can no longer sign it.`)) return;
    try { await api.post(`/esign/envelopes/${e.id}/void`); toast.success("Request voided"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const del = async (e) => {
    if (!window.confirm(`Delete \"${e.title}\"?`)) return;
    try { await api.delete(`/esign/envelopes/${e.id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(apiErr(err)); }
  };
  const download = (e) => downloadFile(`/esign/envelopes/${e.id}/signed`, e.signed_pdf_name || "signed.pdf").catch((err) => toast.error(apiErr(err)));

  if (rows === null) return <Loader />;

  return (
    <div>
      <div className="flex items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-head text-2xl font-extrabold text-slate-900 flex items-center gap-2"><FileSignature className="h-6 w-6 text-primary" /> E-Sign</h1>
          <p className="text-sm text-slate-500">Send documents for electronic signature.</p>
        </div>
        <button data-testid="esign-new" onClick={() => nav("/admin/esign/new")} className="inline-flex items-center gap-2 bg-primary text-white px-4 py-2.5 rounded-xl font-semibold text-sm"><Plus className="h-4 w-4" /> New request</button>
      </div>

      {rows.length === 0 ? (
        <Card className="p-10 text-center text-slate-500"><PenLine className="h-10 w-10 mx-auto text-slate-300 mb-3" />{"No signature requests yet. Click \"New request\" to send your first document."}</Card>
      ) : (
        <Card className="overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wider text-slate-400">
              <tr><th className="px-4 py-3">Document</th><th className="px-4 py-3">Client</th><th className="px-4 py-3">With</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 text-right">Actions</th></tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((e) => (
                <tr key={e.id} data-testid={`esign-row-${e.id}`} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-semibold text-slate-800 cursor-pointer" onClick={() => nav(`/admin/esign/${e.id}`)}>{e.title}</td>
                  <td className="px-4 py-3 text-slate-600">{e.client_name || "—"}</td>
                  <td className="px-4 py-3 text-slate-600">{e.with_whom || "—"}</td>
                  <td className="px-4 py-3"><span className={`text-xs font-semibold px-2 py-1 rounded-full ${BADGE[e.status] || "bg-slate-100"}`}>{e.status}</span></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-end gap-2">
                      <button onClick={() => nav(`/admin/esign/${e.id}`)} className="text-xs font-semibold text-slate-600 hover:text-slate-900">Open</button>
                      {e.status === "Sent" && <button data-testid={`esign-copy-${e.id}`} onClick={() => copyLink(e)} title="Copy signing link" className="text-slate-400 hover:text-primary"><Copy className="h-4 w-4" /></button>}
                      {e.status === "Sent" && <button onClick={() => resend(e)} title="Resend email" className="text-slate-400 hover:text-primary"><Send className="h-4 w-4" /></button>}
                      {e.status === "Completed" && <button data-testid={`esign-download-${e.id}`} onClick={() => download(e)} title="Download signed PDF" className="text-slate-400 hover:text-emerald-600"><Download className="h-4 w-4" /></button>}
                      {(e.status === "Sent" || e.status === "Draft") && <button onClick={() => voidEnv(e)} title="Void" className="text-slate-400 hover:text-rose-600"><Ban className="h-4 w-4" /></button>}
                      {(e.status === "Draft" || e.status === "Voided") && <button onClick={() => del(e)} title="Delete" className="text-slate-400 hover:text-rose-600"><Trash2 className="h-4 w-4" /></button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}
    </div>
  );
}
